/**
 * Easykit inkooporder — drop the mail of Easykit (or the order as a PDF), check
 * what was read out of it, import it.
 *
 * This view owns the flow: choose a file, hold the corrected document, send it
 * back. Everything it renders lives in `components/easykit/Inkooporder*`.
 *
 * Nothing typed here is trusted. `/commit` takes the whole document and re-runs
 * the same rules on it, so a correction goes through the checks rather than
 * around them.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { commitInkooporder, parseInkooporder } from '@/api/easykitInkooporder';
import type {
  Finding,
  InkooporderCommitResult,
  InkooporderDocument,
  InkooporderLine,
  InkooporderParseResult,
  ReviewContext,
} from '@/api/easykitInkooporder';
import { InkooporderFields } from '@/components/easykit/InkooporderFields';
import { InkooporderFindings } from '@/components/easykit/InkooporderFindings';
import { InkooporderLines } from '@/components/easykit/InkooporderLines';
import { InkooporderPlan } from '@/components/easykit/InkooporderPlan';

export function EasykitInkooporderView() {
  const [doc, setDoc] = useState<InkooporderDocument | null>(null);
  const [parsed, setParsed] = useState<InkooporderParseResult | null>(null);
  const [context, setContext] = useState<ReviewContext | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [edited, setEdited] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [committed, setCommitted] = useState<InkooporderCommitResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // The preview is a blob URL; revoking it on replace keeps the tab from holding
  // every PDF the user looked at this session.
  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  const handleFile = useCallback(async (chosen: File) => {
    setLoading(true);
    setError(null);
    setDoc(null);
    setParsed(null);
    setCommitted(null);
    setEdited(false);
    setFile(chosen);
    const droppedPdf = isPdfFile(chosen);
    setPdfUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return droppedPdf ? URL.createObjectURL(chosen) : null;
    });
    try {
      const result = await parseInkooporder(chosen);
      setParsed(result);
      setDoc(result.document);
      setFindings(result.findings);
      setContext(result.context);
      // A message has no PDF the browser can show; the server sends the order
      // it found inside it, so the preview keeps working.
      if (result.orderPdfBase64) {
        const url = URL.createObjectURL(base64ToPdfBlob(result.orderPdfBase64));
        setPdfUrl((previous) => {
          if (previous) URL.revokeObjectURL(previous);
          return url;
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kon het document niet inlezen');
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = () => {
    setDoc(null);
    setParsed(null);
    setContext(null);
    setFindings([]);
    setCommitted(null);
    setError(null);
    setEdited(false);
    setFile(null);
    setPdfUrl((previous) => { if (previous) URL.revokeObjectURL(previous); return null; });
    if (inputRef.current) inputRef.current.value = '';
  };

  const patch = useCallback((change: Partial<InkooporderDocument>) => {
    setDoc((current) => (current ? { ...current, ...change } : current));
    setEdited(true);
  }, []);

  const patchLine = useCallback((index: number, change: Partial<InkooporderLine>) => {
    setDoc((current) => {
      if (!current) return current;
      return { ...current, lines: current.lines.map((line, i) => (i === index ? { ...line, ...change } : line)) };
    });
    setEdited(true);
  }, []);

  const handleImport = useCallback(async () => {
    if (!doc || !file) return;
    setImporting(true);
    setError(null);
    try {
      const result = await commitInkooporder(file, doc, context?.resumableOrderId);
      setCommitted(result);
      setFindings(result.findings);
      setEdited(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Importeren is niet gelukt');
    } finally {
      setImporting(false);
    }
  }, [doc, file, context]);

  const lineSum = useMemo(
    () => (doc ? doc.lines.reduce((total, line) => total + (line.lineTotal || 0), 0) : 0),
    [doc],
  );

  // The same sum the server writes: material lines only, rounded, because Odoo
  // stores the m² total as an integer.
  const totalM2 = useMemo(() => {
    const mapped = new Set((parsed?.intent?.lines ?? []).map((l) => l.articleNumber));
    const source = doc ? doc.lines.filter((l) => mapped.has(l.articleNumber)) : [];
    return Math.round(source.reduce((total, line) => total + (line.quantity || 0), 0));
  }, [doc, parsed]);

  const blocked = findings.some((f) => f.level === 'refuse');
  const canResume = context?.resumableOrderId != null;

  return (
    <div className="px-4 lg:px-8 py-4 lg:py-6">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-lg lg:text-xl font-extrabold text-td-navy">EasyKit Inkooporder</h1>
        <div className="ml-auto flex items-center gap-2">
          {file && <span className="text-xs text-td-muted max-w-[16rem] truncate" title={file.name}>{file.name}</span>}
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,message/rfc822,application/vnd.ms-outlook,.pdf,.msg,.eml"
            className="sr-only"
            id="inkooporder-file"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); }}
          />
          <label
            htmlFor="inkooporder-file"
            className="cursor-pointer rounded-lg bg-td-navy text-white text-sm font-semibold px-4 py-2 hover:bg-td-navy/90"
          >
            {doc ? 'Ander document' : 'Kies bestand'}
          </label>
          {doc && (
            <button type="button" onClick={reset} className="text-sm text-td-muted hover:text-td-navy px-2 py-2">
              Wissen
            </button>
          )}
        </div>
      </div>

      {!doc && !loading && !error && (
        <div
          role="presentation"
          className="cursor-pointer rounded-xl border-2 border-dashed border-gray-300 hover:border-td-magenta/60 bg-white px-6 py-16 text-center transition"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) void handleFile(f); }}
        >
          <p className="text-td-navy font-semibold">Sleep de mail van Easykit hierheen, of klik om te kiezen</p>
          <p className="text-td-muted text-sm mt-1">
            Het volledige bericht (.msg of .eml) of de inkooporder als PDF. Er wordt pas iets aangemaakt als jij op
            Importeren klikt.
          </p>
        </div>
      )}

      {loading && (
        <div className="py-16 text-center">
          <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
            <span className="sr-only">Document wordt ingelezen…</span>
          </div>
          <p className="text-td-muted text-sm mt-3">Document wordt ingelezen…</p>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 text-red-900 px-4 py-3 text-sm mb-4">{error}</div>
      )}

      {doc && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
          {/* The document itself, so every value can be checked against it. */}
          <div className="rounded-xl border border-gray-200 bg-white overflow-hidden xl:sticky xl:top-4">
            {pdfUrl && <iframe src={pdfUrl} title="Inkooporder" className="w-full h-[70vh] xl:h-[80vh]" />}
          </div>

          <div className="space-y-5">
            {committed ? (
              <div className="rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-900 px-4 py-3">
                <p className="font-bold text-sm">
                  Verkooporder{' '}
                  {committed.saleOrderUrl ? (
                    <a
                      href={committed.saleOrderUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="underline decoration-emerald-700/40 hover:decoration-emerald-700"
                    >
                      {committed.saleOrderName}
                    </a>
                  ) : (
                    committed.saleOrderName
                  )}{' '}
                  aangemaakt als concept.
                </p>
                <p className="text-xs mt-0.5">Bevestigen doe je in Odoo.</p>
                <ul className="text-sm mt-1 list-disc list-inside">
                  {committed.steps.map((step) => <li key={step}>{step}</li>)}
                </ul>
                {committed.branchPartnerName && <p className="text-sm mt-1">Filiaal: {committed.branchPartnerName}</p>}
                {committed.dossier?.enabled && (
                  <div className="mt-3 pt-3 border-t border-emerald-200">
                    <p className="font-bold text-sm">
                      {committed.dossier.folderUrl ? (
                        <a
                          href={committed.dossier.folderUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="underline decoration-emerald-700/40 hover:decoration-emerald-700"
                        >
                          Dossier in SharePoint
                        </a>
                      ) : (
                        'Dossier in SharePoint'
                      )}
                    </p>
                    <ul className="text-sm mt-1 list-disc list-inside">
                      {committed.dossier.steps.map((step) => <li key={step}>{step}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <>
                <InkooporderFindings findings={findings} stale={edited} />
                {(context?.existingOrders?.length ?? 0) > 0 && (
                  <div className="mt-2 text-sm text-td-navy">
                    <span className="text-td-muted">Deze referentie staat al op: </span>
                    {context!.existingOrders.map((order, index) => (
                      <span key={order.id}>
                        {index > 0 && ', '}
                        {order.url ? (
                          <a
                            href={order.url}
                            target="_blank"
                            rel="noreferrer"
                            className="font-semibold underline decoration-td-navy/30 hover:decoration-td-navy"
                          >
                            {order.name}
                          </a>
                        ) : (
                          <span className="font-semibold">{order.name}</span>
                        )}
                        <span className="text-td-muted"> ({orderState(order.state)})</span>
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}

            {committed && committed.dossier?.errors.length > 0 && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3">
                <p className="font-bold text-sm">De verkooporder staat in Odoo, het dossier is niet volledig.</p>
                <ul className="text-sm mt-1 list-disc list-inside">
                  {committed.dossier.errors.map((e) => <li key={e}>{e}</li>)}
                </ul>
                <p className="text-xs mt-1">
                  Opnieuw importeren vult aan wat ontbreekt; wat er al staat, blijft staan.
                </p>
              </div>
            )}

            {!committed && (parsed?.mail || parsed?.plan) && (
              <InkooporderPlan
                mail={parsed?.mail ?? null}
                plan={parsed?.plan ?? null}
                documents={parsed?.documents ?? []}
                werfName={doc.delivery.name}
                lineCount={doc.lines.length}
              />
            )}

            <InkooporderFields doc={doc} context={context} totalM2={totalM2} onPatch={patch} />
            <InkooporderLines doc={doc} intent={parsed?.intent ?? null} lineSum={lineSum} onPatchLine={patchLine} />

            {!committed && (
              <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  disabled={importing || (blocked && !canResume)}
                  onClick={() => void handleImport()}
                  className={
                    importing || (blocked && !canResume)
                      ? 'rounded-lg bg-gray-200 text-gray-500 text-sm font-semibold px-5 py-2.5 cursor-not-allowed'
                      : 'rounded-lg bg-td-magenta text-white text-sm font-semibold px-5 py-2.5 hover:bg-td-magenta/90'
                  }
                >
                  {importing ? 'Bezig…' : canResume ? 'Afwerken in Odoo' : 'Importeren'}
                </button>
                <p className="text-xs text-td-muted flex-1 min-w-[12rem]">
                  {canResume
                    ? 'Er staat al een concept voor deze referentie. Afwerken vult aan wat ontbreekt en maakt geen tweede order.'
                    : blocked
                      ? 'Los eerst de geblokkeerde punten hierboven op.'
                      : 'Maakt één concept-verkooporder in Odoo. Bevestigen doe je daar zelf.'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

function base64ToPdfBlob(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: 'application/pdf' });
}

/** Odoo's own state, in the words the portal uses elsewhere. */
function orderState(state: string): string {
  switch (state) {
    case 'draft':
      return 'concept';
    case 'sent':
      return 'offerte verzonden';
    case 'sale':
      return 'bevestigd';
    case 'cancel':
      return 'geannuleerd';
    default:
      return state;
  }
}
